// eTAMS — Payments & settlements (PDF page 9).
// Finance records settlement of approved claims (payment method, UTR,
// settlement date, voucher, reconciliation) — and can still reject, as before.
import React, { useEffect, useMemo, useState } from 'react';
import { expenseAPI, etamsAPI, openExpenseAttachment } from '../../services/api';
import { notify } from '../../utils/notify';
import { claimActorName } from '../../utils/expenseStatus';
import {
  PageShell, PageHead, Stat, Card, Badge, Field, Input, Select, Check, Note, Empty, Loading, Spin, Tabs, FileChip, Pager, usePaged,
  money, pad2, fmtDate, today, promptText, downloadCSV,
} from '../../components/ui';
import { attachmentsOf, netOf } from '../../utils/etams';

function ExpenseFinanceReview() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState({ method: 'Bank transfer', utr: '', date: today(), voucher: '', reconciled: true });
  const [acting, setActing] = useState(null);
  const [targetDays, setTargetDays] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const d = await expenseAPI.listClaims({ limit: 300 });
      setClaims((d.claims || []).filter((c) => ['FINANCE_APPROVED', 'SETTLED'].includes(c.status)));
    } catch (e) { notify.error(e.message || 'Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    etamsAPI.policy().then((d) => setTargetDays(d.policy?.paymentTargetDays || 10)).catch(() => {});
  }, []);

  const pending = claims.filter((c) => c.status === 'FINANCE_APPROVED');
  const settled = claims.filter((c) => c.status === 'SETTLED');
  const rows = useMemo(() => {
    const base = tab === 'pending' ? pending : settled;
    const t = q.trim().toLowerCase();
    return !t ? base : base.filter((c) => [c.claimId, c.reportTitle, c.purposeOfTravel, c.employeeName, c.projectCode].some((v) => String(v || '').toLowerCase().includes(t)));
  }, [claims, tab, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const paged = usePaged(rows, 8);
  const sel = claims.find((c) => c._id === selId) || rows[0] || null;
  useEffect(() => { setForm({ method: 'Bank transfer', utr: '', date: today(), voucher: '', reconciled: true }); }, [sel?._id]);

  const readyToPay = pending.filter((c) => netOf(c) > 0).reduce((s, c) => s + netOf(c), 0);
  const toRecover = pending.filter((c) => netOf(c) < 0).reduce((s, c) => s + Math.abs(netOf(c)), 0);

  const record = async () => {
    if (!sel) return;
    if (form.method !== 'Cash' && !form.utr.trim()) { notify.error('Enter the UTR / reference number'); return; }
    if (!form.date) { notify.error('Select the settlement date'); return; }
    setActing('settle');
    try {
      await expenseAPI.settleClaim(sel._id, {
        settlementAmount: netOf(sel), settlementMode: form.method,
        remarks: form.utr || `Settled via ${form.method}`,
        utrReference: form.utr, voucherReference: form.voucher, settlementDate: form.date, reconciled: form.reconciled,
      });
      notify.success(`Settlement recorded for ${sel.claimId}`);
      setSelId(null);
      await load();
    } catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const reject = async () => {
    const remarks = await promptText({ title: `Reject ${sel.claimId}?`, confirmText: 'Reject', color: '#c43c31' });
    if (remarks === null) return;
    setActing('reject');
    try { await expenseAPI.rejectClaim(sel._id, remarks); notify.success('Claim rejected'); setSelId(null); await load(); } catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const exportCsv = () => downloadCSV('settlements.csv', rows, [
    { label: 'Claim No.', value: 'claimId' }, { label: 'Trip / Purpose', value: (c) => c.reportTitle || c.purposeOfTravel }, { label: 'Employee', value: 'employeeName' },
    { label: 'Project', value: 'projectCode' }, { label: 'Employee expenses', value: 'totalExpenses' }, { label: 'Advance received', value: 'advanceReceived' },
    { label: 'Net payable / paid', value: (c) => (c.status === 'SETTLED' ? c.settlementAmount ?? netOf(c) : netOf(c)) }, { label: 'Status', value: (c) => (c.status === 'SETTLED' ? 'Paid' : 'Approved') },
    { label: 'Mode', value: 'settlementMode' }, { label: 'UTR', value: 'utrReference' }, { label: 'Voucher', value: 'voucherReference' }, { label: 'Settled on', value: (c) => fmtDate(c.settlementDate || c.settledAt) },
  ]);

  return (
    <PageShell section="Travel & Expenses">
      <PageHead title="Payments & settlements" badge={<span className="tse-chip-note">Finance</span>}
        subtitle="Track approved claims, record payments and manage settlements with employees." />

      <div className="tse-grid g-4 mb-18">
        <Stat icon="bi-currency-rupee" tone="green" label="Ready to pay" value={money(readyToPay)} loading={loading} onClick={() => setTab('pending')} />
        <Stat icon="bi-arrow-counterclockwise" tone="amber" label="Advance to recover" value={money(toRecover)} loading={loading} />
        <Stat icon="bi-file-earmark-text" tone="blue" label="Settled claims" value={pad2(settled.length)} loading={loading} onClick={() => setTab('settled')} />
        <Stat icon="bi-calendar3" tone="purple" label="Payment target" value={`${targetDays} days`} />
      </div>

      <div className="tse-split mb-18" style={{ gridTemplateColumns: 'minmax(0, 1fr) 420px' }}>
        <Card navy bodyClass={null} title="Approved claims for settlement" actions={<button type="button" className="tse-btn xs" style={{ background: 'rgba(255,255,255,.14)', borderColor: 'rgba(255,255,255,.3)' }} onClick={exportCsv}><i className="bi bi-download" />Export</button>} style={{ minHeight: 380 }}>
          <div className="tse-toolbar" style={{ paddingBottom: 0 }}>
            <Tabs value={tab} onChange={(k) => { setTab(k); setSelId(null); }} tabs={[{ key: 'pending', label: 'Awaiting payment', count: pending.length }, { key: 'settled', label: 'Paid', count: settled.length }]} />
            <div className="tse-spacer" />
            <div className="tse-search" style={{ maxWidth: 260 }}><i className="bi bi-search" /><input placeholder="Search claims" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          </div>
          {loading ? <Loading /> : !rows.length ? <Empty icon="bi-inbox" title={tab === 'pending' ? 'No claims awaiting payment' : 'No settled claims yet'} /> : (
            <div className="tse-table-wrap">
              <table className="tse-table bordered">
                <thead><tr><th>Trip / Purpose</th><th>Claim No.</th><th>Project</th><th className="num">Employee<br />expenses (₹)</th><th className="num">Advance<br />received (₹)</th><th className="num">Net payable /<br />Net paid (₹)</th><th>Status</th></tr></thead>
                <tbody>
                  {paged.rows.map((c) => (
                    <tr key={c._id} className={`clickable ${sel?._id === c._id ? 'selected' : ''}`} onClick={() => setSelId(c._id)}>
                      <td><div className="tse-ellipsis" style={{ maxWidth: 220 }}>{c.reportTitle || c.purposeOfTravel}</div><div className="tse-small tse-muted">{c.employeeName}</div></td>
                      <td>{c.claimId}</td>
                      <td>{c.projectCode || '—'}</td>
                      <td className="num">{(c.totalExpenses || 0).toLocaleString('en-IN')}</td>
                      <td className="num">{(c.advanceReceived || 0).toLocaleString('en-IN')}</td>
                      <td className="num" style={{ color: netOf(c) < 0 ? '#c0392b' : undefined }}>{(c.status === 'SETTLED' ? (c.settlementAmount ?? netOf(c)) : netOf(c)).toLocaleString('en-IN')}</td>
                      <td>{c.status === 'SETTLED' ? <Badge tone="paid" pill>Paid</Badge> : <Badge tone="approved" pill>Approved</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager {...paged} label="claims" />
        </Card>

        <Card navy title="Payment record">
          {!sel ? <Empty icon="bi-hand-index" title="Select a claim" /> : sel.status === 'SETTLED' ? (
            <div className="tse-stack" style={{ gap: 4 }}>
              <div className="tse-kv-row"><span className="k">Claim</span><span className="v">{sel.reportTitle || sel.purposeOfTravel} ({sel.claimId})</span></div>
              <div className="tse-kv-row"><span className="k">Payment method</span><span className="v">{sel.settlementMode || '—'}</span></div>
              <div className="tse-kv-row"><span className="k">UTR / Reference</span><span className="v">{sel.utrReference || '—'}</span></div>
              <div className="tse-kv-row"><span className="k">Settlement date</span><span className="v">{fmtDate(sel.settlementDate || sel.settledAt)}</span></div>
              <div className="tse-kv-row"><span className="k">Voucher</span><span className="v">{sel.voucherReference || '—'}</span></div>
              <div className="tse-kv-row"><span className="k">Reconciled</span><span className="v">{sel.reconciled ? 'Yes' : 'No'}</span></div>
              <div className="tse-kv-row total"><span className="k">Net paid</span><span className="v">{money(sel.settlementAmount ?? netOf(sel))}</span></div>
              <div className="tse-small tse-muted" style={{ marginTop: 8 }}>Settled by {claimActorName(sel) || 'Finance'}</div>
            </div>
          ) : (
            <div className="tse-stack" style={{ gap: 8 }}>
              <Field label="Claim" inline><Input value={`${sel.reportTitle || sel.purposeOfTravel} (${sel.claimId})`} readOnly /></Field>
              <Field label="Payment method" inline><Select value={form.method} onChange={(e) => setForm((p) => ({ ...p, method: e.target.value }))} options={['Bank transfer', 'NEFT', 'RTGS', 'UPI', 'Cheque', 'Cash']} /></Field>
              <Field label="UTR / Reference no." inline><Input value={form.utr} onChange={(e) => setForm((p) => ({ ...p, utr: e.target.value }))} placeholder="Enter UTR or reference number" /></Field>
              <Field label="Settlement date" inline><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} /></Field>
              <Field label="Voucher reference" inline><Input value={form.voucher} onChange={(e) => setForm((p) => ({ ...p, voucher: e.target.value }))} placeholder="Enter voucher reference" /></Field>
              <Check checked={form.reconciled} onChange={(v) => setForm((p) => ({ ...p, reconciled: v }))} label="Mark as reconciled in accounts" />
              {attachmentsOf(sel).length > 0 && <div className="tse-stack" style={{ gap: 6 }}>{attachmentsOf(sel).slice(0, 3).map((a, i) => <FileChip key={a.s3Key || i} file={a} onOpen={() => openExpenseAttachment(a).catch(() => notify.error('Could not open'))} />)}</div>}
              <button type="button" className="tse-btn lg block" onClick={record} disabled={!!acting}>{acting === 'settle' ? <Spin /> : null}{netOf(sel) < 0 ? 'Record recovery' : 'Record settlement'}</button>
              <button type="button" className="tse-btn danger-outline block" onClick={reject} disabled={!!acting}>{acting === 'reject' ? <Spin /> : null}Reject claim</button>
            </div>
          )}
        </Card>
      </div>

      {sel && (
        <div className="tse-grid" style={{ gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 0.8fr)' }}>
          <Card>
            <div className="tse-row" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <span className="ic blue" style={{ width: 46, height: 46, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.326rem' }}><i className="bi bi-calculator" /></span>
              <div style={{ flex: 1 }}>
                <div className="tse-strong" style={{ marginBottom: 8 }}>Settlement calculation <i className="bi bi-info-circle tse-muted" /></div>
                <div className="tse-row" style={{ background: '#f1f6fb', borderRadius: 8, padding: '8px 13px', justifyContent: 'space-around', textAlign: 'center' }}>
                  <div><div className="tse-small tse-muted">Approved employee expenses</div><div style={{ fontSize: '1.092rem', fontWeight: 700 }}>{money(sel.totalExpenses)}</div></div>
                  <div style={{ fontSize: '1.092rem' }}>−</div>
                  <div><div className="tse-small tse-muted">Advance received</div><div style={{ fontSize: '1.092rem', fontWeight: 700 }}>{money(sel.advanceReceived)}</div></div>
                  <div style={{ fontSize: '1.092rem' }}>=</div>
                  <div><div className="tse-small tse-muted">{netOf(sel) < 0 ? 'Recoverable' : 'Net payable'}</div><div style={{ fontSize: '1.092rem', fontWeight: 700 }}>{money(Math.abs(netOf(sel)))}</div></div>
                </div>
                {sel.amountModified && <div className="tse-small tse-muted" style={{ marginTop: 6 }}>Net amount revised during approval to {money(netOf(sel))}.</div>}
              </div>
            </div>
          </Card>
          <Note tone="warn" big title={netOf(sel) < 0 ? 'Recovery from employee' : 'Settlement notifies the employee'}>
            {netOf(sel) < 0 ? 'The advance exceeded the approved expenses — recover the balance via payroll or cash deposit before closing.' : 'Recording a settlement marks the claim Paid and emails the employee with the reference.'}
          </Note>
        </div>
      )}
    </PageShell>
  );
}
export default ExpenseFinanceReview;
